import { Close } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname } from "../../../utils";

interface SubscriptionTier {
  id: string;
  tier_name: string;
  tier_type: string;
  tier_description: string;
  credit_limit: number;
  document_limit: number;
  seats: number;
  companies: number;
  pricing: any;
  has_configuration?: boolean;
}

interface QuoteCreationDialogProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  whitelabelId: string;
  onQuoteCreated: () => void;
}

export default function QuoteCreationDialog({
  open,
  onClose,
  getToken,
  whitelabelId,
  onQuoteCreated,
}: QuoteCreationDialogProps) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier | null>(
    null
  );
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "annual">(
    "monthly"
  );
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Custom pricing overrides
  const [customPrice, setCustomPrice] = useState<number | null>(null);

  // Add-on configuration
  const [seats, setSeats] = useState<number>(1);
  const [companies, setCompanies] = useState<number>(1);
  const [creditPackages, setCreditPackages] = useState<number>(0);

  useEffect(() => {
    if (open) {
      loadTiers();
    }
  }, [open]);

  // Update custom price when add-ons change
  useEffect(() => {
    if (customPrice !== null && selectedTier) {
      const minPrice = calculateBasePrice();
      // Always update to match the calculated price when add-ons change
      setCustomPrice(minPrice);
    }
  }, [seats, companies, creditPackages, billingPeriod, selectedTier]);

  const loadTiers = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/whitelabel/${whitelabelId}/subscription-tiers`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Filter out free and system tiers
      const paidTiers = (response.data.tiers || []).filter(
        (tier: SubscriptionTier) =>
          tier.tier_type !== "free" && !tier.tier_type.includes("system")
      );

      setTiers(paidTiers);

      // Select first tier by default
      if (paidTiers.length > 0) {
        const firstTier = paidTiers[0];
        setSelectedTier(firstTier);
        // Initialize add-ons with tier defaults
        setSeats(firstTier.seats || 1);
        setCompanies(firstTier.companies || 1);
        setCreditPackages(0);
      }
    } catch (err: any) {
      console.error("Error loading tiers:", err);
      setError("Failed to load subscription tiers");
    } finally {
      setLoading(false);
    }
  };

  const calculateBasePrice = (): number => {
    if (!selectedTier || !selectedTier.pricing) {
      return 0;
    }

    const pricing = selectedTier.pricing;
    const periodPricing = pricing[billingPeriod];

    let basePrice = 0;
    if (periodPricing && periodPricing.price !== undefined) {
      basePrice = periodPricing.price;
    }

    // Calculate add-on costs
    let addOnCost = 0;

    // Additional companies (beyond tier base)
    const baseCompanies = selectedTier.companies || 0;
    const additionalCompanies = Math.max(0, companies - baseCompanies);
    if (additionalCompanies > 0) {
      const companyPrice = billingPeriod === "annual" ? 80 : 100; // $100/month or $80/month annual
      addOnCost += additionalCompanies * companyPrice;
    }

    // Additional seats (beyond tier base)
    const baseSeats = selectedTier.seats || 1;
    const additionalSeats = Math.max(0, seats - baseSeats);
    if (additionalSeats > 0) {
      const seatPrice = billingPeriod === "annual" ? 48 : 60; // $60/month or $48/month annual
      addOnCost += additionalSeats * seatPrice;
    }

    // Credit packages
    if (creditPackages > 0) {
      const creditPrice = billingPeriod === "annual" ? 80 : 100; // $100/month or $80/month annual
      addOnCost += creditPackages * creditPrice;
    }

    return basePrice + addOnCost;
  };

  const getDisplayPrice = (): number => {
    if (customPrice !== null) {
      return customPrice;
    }

    return calculateBasePrice();
  };

  const getTotalAmount = (): number => {
    const price = getDisplayPrice();

    if (billingPeriod === "annual") {
      return price * 12;
    }

    return price;
  };

  const calculatePaymentSplit = () => {
    if (!selectedTier || !selectedTier.pricing) {
      return { platformAmount: 0, whitelabelMargin: 0 };
    }

    const pricing = selectedTier.pricing;
    const periodPricing = pricing[billingPeriod];

    // Get base platform price
    let platformPrice = periodPricing?.platform_price || 0;

    // For annual billing, multiply by 12
    if (billingPeriod === "annual") {
      platformPrice = platformPrice * 12;
    }

    // Calculate add-on platform costs (50% of add-on costs)
    let addOnPlatformCost = 0;

    // Additional companies
    const baseCompanies = selectedTier.companies || 0;
    const additionalCompanies = Math.max(0, companies - baseCompanies);
    if (additionalCompanies > 0) {
      const companyPrice = billingPeriod === "annual" ? 80 : 100;
      const companyCost = additionalCompanies * companyPrice * 0.5;
      addOnPlatformCost += billingPeriod === "annual" ? companyCost * 12 : companyCost;
    }

    // Additional seats
    const baseSeats = selectedTier.seats || 1;
    const additionalSeats = Math.max(0, seats - baseSeats);
    if (additionalSeats > 0) {
      const seatPrice = billingPeriod === "annual" ? 48 : 60;
      const seatCost = additionalSeats * seatPrice * 0.5;
      addOnPlatformCost += billingPeriod === "annual" ? seatCost * 12 : seatCost;
    }

    // Credit packages
    if (creditPackages > 0) {
      const creditPrice = billingPeriod === "annual" ? 80 : 100;
      const creditCost = creditPackages * creditPrice * 0.5;
      addOnPlatformCost += billingPeriod === "annual" ? creditCost * 12 : creditCost;
    }

    const totalPlatformAmount = platformPrice + addOnPlatformCost;
    const totalAmount = getTotalAmount();
    const whitelabelMargin = totalAmount - totalPlatformAmount;

    return {
      platformAmount: Math.round(totalPlatformAmount * 100) / 100,
      whitelabelMargin: Math.round(whitelabelMargin * 100) / 100,
    };
  };

  const handleCreate = async () => {
    if (!selectedTier) {
      setError("Please select a subscription tier");
      return;
    }

    if (!firstName || !lastName || !email) {
      setError("Please fill in all customer information");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const token = await getToken();

      // Calculate total credits including packages
      const creditIncrement = 3000; // Standard credit increment per package
      const totalCredits = selectedTier.credit_limit + (creditPackages * creditIncrement);

      // Calculate payment split
      const paymentSplit = calculatePaymentSplit();

      const pricingData = {
        plan: selectedTier.tier_type,
        tierName: selectedTier.tier_name,
        tierType: selectedTier.tier_type,
        billingPeriod: billingPeriod,
        monthlyPrice: getDisplayPrice(),
        totalAmount: getTotalAmount(),
        creditLimit: totalCredits,
        documentLimit: selectedTier.document_limit * seats, // Scale with seats
        seats: seats,
        companies: companies,
        creditPackages: creditPackages,
        pricing: selectedTier.pricing,
        // Store base tier values for reference
        baseCreditLimit: selectedTier.credit_limit,
        baseSeats: selectedTier.seats,
        baseCompanies: selectedTier.companies,
        // Payment split
        platformAmount: paymentSplit.platformAmount,
        whitelabelMargin: paymentSplit.whitelabelMargin,
      };

      await axios.post(
        `${hostname}/whitelabel/${whitelabelId}/quotes`,
        {
          firstName,
          lastName,
          email,
          notes,
          pricingData,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      onQuoteCreated();
      handleClose();
    } catch (err: any) {
      console.error("Error creating quote:", err);
      setError(err.response?.data?.error || "Failed to create quote");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setNotes("");
    setSelectedTier(null);
    setBillingPeriod("monthly");
    setCustomPrice(null);
    setSeats(1);
    setCompanies(1);
    setCreditPackages(0);
    setError(null);
    onClose();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Create Custom Quote
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Typography>Loading tiers...</Typography>
        ) : (
          <Box>
            {/* Customer Information */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Customer Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Internal Notes (Optional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  multiline
                  rows={2}
                  helperText="Notes are only visible to you, not the customer"
                />
              </Grid>
            </Grid>

            {/* Pricing Configuration */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Pricing Configuration
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Subscription Tier</InputLabel>
                  <Select
                    value={selectedTier?.id || ""}
                    onChange={(e) => {
                      const tier = tiers.find((t) => t.id === e.target.value);
                      setSelectedTier(tier || null);
                      setCustomPrice(null); // Reset custom price
                      // Reset add-ons to tier defaults
                      if (tier) {
                        setSeats(tier.seats || 1);
                        setCompanies(tier.companies || 1);
                        setCreditPackages(0);
                      }
                    }}
                    label="Subscription Tier"
                  >
                    {tiers.map((tier) => (
                      <MenuItem key={tier.id} value={tier.id}>
                        {tier.tier_name} - {tier.credit_limit.toLocaleString()}{" "}
                        credits
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Billing Period</InputLabel>
                  <Select
                    value={billingPeriod}
                    onChange={(e) =>
                      setBillingPeriod(e.target.value as "monthly" | "annual")
                    }
                    label="Billing Period"
                  >
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="annual">Annual</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Custom Price"
                  type="number"
                  value={customPrice !== null ? customPrice : calculateBasePrice()}
                  onChange={(e) => {
                    const value = e.target.value ? parseFloat(e.target.value) : null;
                    const minPrice = calculateBasePrice();

                    // Set to minimum if null or if under the minimum
                    if (value === null || value < minPrice) {
                      setCustomPrice(minPrice);
                    } else {
                      setCustomPrice(value);
                    }
                  }}
                  onFocus={(e) => {
                    // Set custom price if not already set, then select the text
                    if (customPrice === null) {
                      setCustomPrice(calculateBasePrice());
                    }
                    // Select the text after a short delay so user can immediately type
                    setTimeout(() => e.target.select(), 0);
                  }}
                  inputProps={{
                    min: calculateBasePrice(),
                    step: 1,
                  }}
                  helperText={`Minimum price: ${formatCurrency(
                    calculateBasePrice()
                  )}/month (including add-ons). Click to override.`}
                />
              </Grid>
            </Grid>

            {/* Add-on Configuration */}
            {selectedTier && (
              <>
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  Add-on Configuration
                </Typography>
                <Grid container spacing={3}>
                  {/* Companies Slider */}
                  <Grid item xs={12}>
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Companies
                        </Typography>
                        <Typography variant="body2" fontWeight="500">
                          {companies}
                        </Typography>
                      </Box>
                      <Slider
                        value={companies}
                        onChange={(_, newValue) => setCompanies(newValue as number)}
                        min={selectedTier.companies || 1}
                        max={30}
                        step={1}
                        marks={[
                          { value: selectedTier.companies || 1, label: `${selectedTier.companies || 1}` },
                          { value: 10, label: "10" },
                          { value: 20, label: "20" },
                          { value: 30, label: "30" },
                        ]}
                        valueLabelDisplay="auto"
                      />
                      <Typography variant="caption" color="text.secondary">
                        Base: {selectedTier.companies || 1} companies included.
                        {companies > (selectedTier.companies || 1) &&
                          ` Additional cost: ${formatCurrency(
                            (companies - (selectedTier.companies || 1)) *
                              (billingPeriod === "annual" ? 80 : 100)
                          )}/month`}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Seats Slider */}
                  <Grid item xs={12}>
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Seats
                        </Typography>
                        <Typography variant="body2" fontWeight="500">
                          {seats}
                        </Typography>
                      </Box>
                      <Slider
                        value={seats}
                        onChange={(_, newValue) => setSeats(newValue as number)}
                        min={selectedTier.seats || 1}
                        max={60}
                        step={1}
                        marks={[
                          { value: selectedTier.seats || 1, label: `${selectedTier.seats || 1}` },
                          { value: 15, label: "15" },
                          { value: 30, label: "30" },
                          { value: 45, label: "45" },
                          { value: 60, label: "60" },
                        ]}
                        valueLabelDisplay="auto"
                      />
                      <Typography variant="caption" color="text.secondary">
                        Base: {selectedTier.seats || 1} seats included.
                        {seats > (selectedTier.seats || 1) &&
                          ` Additional cost: ${formatCurrency(
                            (seats - (selectedTier.seats || 1)) *
                              (billingPeriod === "annual" ? 48 : 60)
                          )}/month`}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Credit Packages Slider */}
                  <Grid item xs={12}>
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Credit Packages (3,000 credits each)
                        </Typography>
                        <Typography variant="body2" fontWeight="500">
                          {creditPackages}
                        </Typography>
                      </Box>
                      <Slider
                        value={creditPackages}
                        onChange={(_, newValue) =>
                          setCreditPackages(newValue as number)
                        }
                        min={0}
                        max={10}
                        step={1}
                        marks={[
                          { value: 0, label: "0" },
                          { value: 5, label: "5" },
                          { value: 10, label: "10" },
                        ]}
                        valueLabelDisplay="auto"
                      />
                      <Typography variant="caption" color="text.secondary">
                        Base: {selectedTier.credit_limit.toLocaleString()} credits included.
                        {creditPackages > 0 &&
                          ` Additional: ${(creditPackages * 3000).toLocaleString()} credits for ${formatCurrency(
                            creditPackages * (billingPeriod === "annual" ? 80 : 100)
                          )}/month`}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}

            {/* Summary */}
            {selectedTier && (
              <Box sx={{ mt: 3, p: 2, bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Quote Summary
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Plan:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      {selectedTier.tier_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Credits:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      {(selectedTier.credit_limit + (creditPackages * 3000)).toLocaleString()}
                      {creditPackages > 0 && (
                        <Typography component="span" variant="caption" color="text.secondary">
                          {" "}(base: {selectedTier.credit_limit.toLocaleString()} + {(creditPackages * 3000).toLocaleString()})
                        </Typography>
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Seats:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      {seats}
                      {seats > (selectedTier.seats || 1) && (
                        <Typography component="span" variant="caption" color="text.secondary">
                          {" "}(base: {selectedTier.seats} + {seats - (selectedTier.seats || 1)})
                        </Typography>
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Companies:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      {companies}
                      {companies > (selectedTier.companies || 1) && (
                        <Typography component="span" variant="caption" color="text.secondary">
                          {" "}(base: {selectedTier.companies} + {companies - (selectedTier.companies || 1)})
                        </Typography>
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Billing:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      {billingPeriod === "annual" ? "Annual" : "Monthly"}
                    </Typography>
                  </Grid>

                  {/* Divider */}
                  <Grid item xs={12}>
                    <Box sx={{ borderTop: 1, borderColor: "divider", my: 1 }} />
                  </Grid>

                  {/* Payment Split */}
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Platform Fee:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      {formatCurrency(calculatePaymentSplit().platformAmount)}
                      {billingPeriod === "annual" ? "/year" : "/month"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Your Margin:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="success.main" fontWeight="medium">
                      {formatCurrency(calculatePaymentSplit().whitelabelMargin)}
                      {billingPeriod === "annual" ? "/year" : "/month"}
                    </Typography>
                  </Grid>

                  {/* Divider */}
                  <Grid item xs={12}>
                    <Box sx={{ borderTop: 1, borderColor: "divider", my: 1 }} />
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body1" fontWeight="bold">
                      Total Amount:
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body1" fontWeight="bold">
                      {formatCurrency(getTotalAmount())}
                      {billingPeriod === "annual" ? "/year" : "/month"}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <LoadingButton
          onClick={handleCreate}
          variant="contained"
          loading={saving}
          disabled={!selectedTier || !firstName || !lastName || !email}
        >
          Create Quote
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
